import { z } from 'zod';

export const TTS_PROVIDERS = ['local', 'elevenlabs'] as const;
export type TtsProviderId = (typeof TTS_PROVIDERS)[number];

export const wordTimingSchema = z.object({
  word: z.string().min(1),
  startMs: z.number().min(0),
  endMs: z.number().min(0),
  charStart: z.number().int().min(0),
  charEnd: z.number().int().min(0),
});

export const sentenceTimingSchema = z.object({
  text: z.string().min(1),
  startMs: z.number().min(0),
  endMs: z.number().min(0),
  charStart: z.number().int().min(0),
  charEnd: z.number().int().min(0),
});

export const pronunciationEntrySchema = z.object({
  written: z.string().trim().min(1),
  pronunciation: z.string().trim().min(1),
  alphabet: z.enum(['alias', 'ipa']).default('alias'),
  locale: z.string().default(''),
});

export const pronunciationDictionaryLocatorSchema = z.object({
  id: z.string().min(1),
  versionId: z.string().min(1),
});

export const ttsCourseSettingsSchema = z.object({
  provider: z.enum(TTS_PROVIDERS).default('elevenlabs'),
  voiceId: z.string().default(''),
  modelId: z.string().default('eleven_multilingual_v2'),
  /** ElevenLabs `voice_settings.speed` range, verified 2026-08-21. */
  rate: z.number().min(0.7).max(1.2).default(1),
  pitch: z.number().min(-1).max(1).default(0),
  volume: z.number().min(0).max(1).default(1),
  pauseAfterSentenceMs: z.number().int().min(0).max(5_000).default(250),
  syntheticDisclosure: z.boolean().default(true),
  lexiconVersion: z.number().int().min(0).default(0),
  lexicon: z.array(pronunciationEntrySchema).default([]),
  /** Latest ElevenLabs dictionary version created from `lexicon`. */
  pronunciationDictionary: pronunciationDictionaryLocatorSchema.optional(),
});

export const slideTtsSettingsSchema = z.object({
  textSource: z.enum(['notes', 'script', 'body']).default('notes'),
  script: z.string().default(''),
  voiceId: z.string().optional(),
  modelId: z.string().optional(),
  /** SHA-256 of the effective source text, used to expose stale audio. */
  sourceHash: z.string().optional(),
  /** Full synthesis cache key used by the take currently on this slide. */
  synthesisKey: z.string().optional(),
  generatedAt: z.string().optional(),
  wordTimings: z.array(wordTimingSchema).default([]),
  sentenceTimings: z.array(sentenceTimingSchema).default([]),
  /** Character offsets in the narration text, indexed by animation click step. */
  animationTriggerOffsets: z.array(z.number().int().min(0)).default([]),
});

export type WordTiming = z.infer<typeof wordTimingSchema>;
export type SentenceTiming = z.infer<typeof sentenceTimingSchema>;
export type PronunciationEntry = z.infer<typeof pronunciationEntrySchema>;
export type PronunciationDictionaryLocator = z.infer<typeof pronunciationDictionaryLocatorSchema>;
export type TtsCourseSettings = z.infer<typeof ttsCourseSettingsSchema>;
export type SlideTtsSettings = z.infer<typeof slideTtsSettingsSchema>;
